import { Array } from "@siteimprove/alfa-array";
import { Element, Text } from "@siteimprove/alfa-dom";
import { Refinement } from "@siteimprove/alfa-refinement";
import { Sequence } from "@siteimprove/alfa-sequence";
import { Anchored } from "./anchored.js";
import * as predicate from "./cell/predicate.js";
const { and, or } = Refinement;
const { isElement } = Element;
const { isText } = Text;
/**
 * {@link https://html.spec.whatwg.org/#concept-cell}
 *
 * @public
 */
export class Cell {
    _element;
    _anchor;
    _width;
    _height;
    _headers;
    constructor(element, anchor, width, height, headers) {
        this._element = element;
        this._anchor = anchor;
        this._width = width;
        this._height = height;
        this._headers = headers;
    }
    get element() {
        return this._element;
    }
    get anchor() {
        return this._anchor;
    }
    get x() {
        return this._anchor.x;
    }
    get y() {
        return this._anchor.y;
    }
    get width() {
        return this._width;
    }
    get height() {
        return this._height;
    }
    get headers() {
        return Sequence.from(this._headers);
    }
    /**
     * {@link https://html.spec.whatwg.org/#empty-cell}
     */
    isEmpty() {
        return this._element.children().none(or(isElement, and(isText, (text) => text.data.trim().length > 0)));
    }
    compare(anchored) {
        return Anchored.compare(this, anchored);
    }
    equals(value) {
        return (value instanceof Cell &&
            value._element.equals(this._element) &&
            value._anchor.equals(this._anchor) &&
            value._width === this._width &&
            value._height === this._height &&
            Array.equals(value._headers, this._headers));
    }
}
/**
 * @public
 */
(function (Cell) {
    function isCell(value) {
        return value instanceof Cell;
    }
    Cell.isCell = isCell;
    Cell.hasElement = predicate.hasElement;
    class Data extends Cell {
        static of(element, anchor, width, height, headers = []) {
            return new Data(element, anchor, width, height, Array.sort(Array.copy(Array.from(headers))));
        }
        constructor(element, anchor, width, height, headers) {
            super(element, anchor, width, height, headers);
        }
        isData() {
            return true;
        }
        isHeader() {
            return false;
        }
        equals(value) {
            return value instanceof Data && super.equals(value);
        }
        toJSON() {
            return {
                type: "data",
                element: this._element.path(),
                anchor: this._anchor.toJSON(),
                width: this._width,
                height: this._height,
                headers: Array.toJSON(this._headers),
            };
        }
    }
    Cell.Data = Data;
    (function (Data) {
        function isData(value) {
            return value instanceof Data;
        }
        Data.isData = isData;
    })(Data = Cell.Data || (Cell.Data = {}));
    Cell.data = Data.of, Cell.isData = Data.isData;
    class Header extends Cell {
        static of(element, anchor, width, height, headers = [], scope = "auto") {
            return new Header(element, anchor, width, height, Array.sort(Array.copy(Array.from(headers))), scope);
        }
        _scope;
        constructor(element, anchor, width, height, headers, scope) {
            super(element, anchor, width, height, headers);
            this._scope = scope;
        }
        get scope() {
            return this._scope;
        }
        isData() {
            return false;
        }
        isHeader() {
            return true;
        }
        equals(value) {
            return (value instanceof Header &&
                super.equals(value) &&
                value._scope === this._scope);
        }
        toJSON() {
            return {
                type: "header",
                scope: this._scope,
                element: this._element.path(),
                anchor: this._anchor.toJSON(),
                width: this._width,
                height: this._height,
                headers: Array.toJSON(this._headers),
            };
        }
    }
    Cell.Header = Header;
    (function (Header) {
        function isHeader(value) {
            return value instanceof Header;
        }
        Header.isHeader = isHeader;
    })(Header = Cell.Header || (Cell.Header = {}));
    Cell.header = Header.of, Cell.isHeader = Header.isHeader;
})(Cell || (Cell = {}));
//# sourceMappingURL=cell.js.map